import { Controller } from "@hotwired/stimulus"
import SelectController from "./select_controller"

// Handles confirmation dialogs when removing companies or record types
// that have labels in use. Usage data is embedded in the form via data attribute.
export default class extends Controller {

  static values = {
    usageData: Object
  }

  connect() {
    // Find select elements by name
    this.companySelect = this.element.querySelector('select[name="label_type[allowed_company_ids][]"]')
    this.recordTypeSelect = this.element.querySelector('select[name="label_type[applicable_record_types][]"]')

    this.boundHandleSubmit = this.handleFormSubmit.bind(this)
    this.element.addEventListener('submit', this.boundHandleSubmit)
  }

  disconnect() {
    this.element.removeEventListener('submit', this.boundHandleSubmit)
  }

  handleFormSubmit(event) {
    if (!this.hasUsageDataValue || !this.usageDataValue) {
      return
    }

    const confirmationMessages = this.buildConfirmationMessages()

    if (confirmationMessages.length > 0) {
      event.preventDefault()
      event.stopPropagation()

      const message = confirmationMessages.join('\n\n') + '\n\nAre you sure you want to continue?'

      if (confirm(message)) {
        this.element.removeEventListener('submit', this.boundHandleSubmit)
        this.element.requestSubmit(event.submitter)
      }
    }
  }

  buildConfirmationMessages() {
    const confirmationMessages = []
    
    // Check for removed companies
    const removedCompanies = this.getRemovedCompaniesInUse()
    if (removedCompanies.length > 0) {
      const companyNames = removedCompanies.map(id => this.usageDataValue.companies[id]).filter(Boolean)
      if (companyNames.length > 0) {
        confirmationMessages.push(
          `Removing ${companyNames.join(', ')} will remove this label from all records in ${companyNames.length === 1 ? 'that company' : 'those companies'}.`
        )
      }
    }

    // Check for removed record types
    const removedRecordTypes = this.getRemovedRecordTypesInUse()
    if (removedRecordTypes.length > 0) {
      const displayNames = removedRecordTypes.map(type => this.getRecordTypeDisplayName(type))
      confirmationMessages.push(
        `Saving the current configuration will remove this label from: ${displayNames.join(', ')}.`
      )
    }
    
    return confirmationMessages
  }

  getRemovedCompaniesInUse() {
    const usageData = this.usageDataValue
    if (!usageData?.company_ids?.length) {
      return []
    }

    if (!this.companySelect?.tomselect) {
      return []
    }

    const selectedItems = this.companySelect.tomselect.items.map(id => String(id))
    if (selectedItems.includes(SelectController.allOptionId)) {
      return []
    }

    const currentItems = new Set(selectedItems.map(id => parseInt(id, 10)))
    const companiesInUse = new Set(usageData.company_ids)

    return [...companiesInUse].filter(id => !currentItems.has(id))
  }

  getRemovedRecordTypesInUse() {
    const usageData = this.usageDataValue
    if (!usageData?.record_types?.length) {
      return []
    }

    if (!this.recordTypeSelect?.tomselect) {
      return []
    }

    const currentItems = new Set(this.recordTypeSelect.tomselect.items)
    if (currentItems.has(SelectController.allOptionId)) {
      return []
    }

    const recordTypesInUse = new Set(usageData.record_types)

    return [...recordTypesInUse].filter(type => !currentItems.has(type))
  }

  getRecordTypeDisplayName(type) {
    const displayNames = {
      'Article': 'Article',
      'Asset': 'Asset',
      'AssetPassword': 'Password',
      'Website': 'Website',
      'IpAddress': 'IP Address',
      'Vlan': 'VLAN',
      'VlanZone': 'VLAN Zone',
      'Procedure': 'Process',
      'Network': 'Network',
      'RackStorage': 'Rack'
    }
    return displayNames[type] || type
  }
}
